import React from 'react';
import { ArrowLink, type ArrowDirection } from '../ArrowLink/ArrowLink';
import { Image } from '../Image/Image';
import { hasCtaDestination } from '../../utils/ctaAction';
import type { CtaAction } from '../../utils/ctaAction';
import { markQuoteElement, stripQuoteMarks } from '../../utils/quoteMarks';
import './TestimonialContent.scss';

/**
 * Props for {@link TestimonialContent}.
 */
export interface TestimonialContentProps {
  /** Extra class on the root, for the host block's own padding, background and alignment. */
  className?: string;
  /** Quote text; any surrounding quotation marks are stripped and redrawn in the page language's style. */
  quote: string;
  /** Person's name. */
  name?: string;
  /** Person's title or role. */
  title?: string;
  /** Short label above the quote. Ignored when `logoSrc` is set. */
  eyebrow?: string;
  /** Brand logo above the quote. Takes precedence over `eyebrow`. */
  logoSrc?: string;
  /** Accessible name for the logo. Falls back to "Brand logo for {name}", or "Brand logo" without a name. */
  logoAlt?: string;
  /** Card heading between the eyebrow and the quote. */
  heading?: string;
  /** Headshot image URL. */
  headshotSrc?: string;
  /** Headshot alt text. Falls back to `name`. */
  headshotAlt?: string;
  /** Link text below the attribution. The link renders when the selected action has its required destination. */
  linkText?: string;
  /** Link URL; the video URL when `linkAction` is `video`. Not used for Popup actions. */
  linkHref?: string;
  /** What the link does: navigate, open a video modal, download, or open a Popup. */
  linkAction?: CtaAction;
  /** Popup ID to open when `linkAction` is `popup`. */
  linkTargetPopupId?: string;
  /** Arrow direction for a plain link. Video and download links show their action icon instead. */
  linkDirection?: ArrowDirection;
  /** Opens a plain link in a new tab. Ignored for video and download links, which stay on the page. */
  linkOpensNewTab?: boolean;
  /** Called when a video link opens its modal. */
  onVideoOpen?: () => void;
  /** Called when a video link's modal closes. */
  onVideoClose?: () => void;
  /** Rendered after the link, e.g. a host block's CTA buttons. */
  children?: React.ReactNode;
}

/**
 * The eyebrow, heading, quote, attribution and link shared by every testimonial. The host block
 * owns the surrounding layout and sets the `--poly-*-testimonialContent-*` custom properties
 * (listed in TestimonialContent.scss) on an ancestor at each breakpoint to size the content.
 */
export const TestimonialContent: React.FC<TestimonialContentProps> = ({
  className,
  quote,
  name,
  title,
  eyebrow,
  logoSrc,
  logoAlt,
  heading,
  headshotSrc,
  headshotAlt,
  linkText,
  linkHref,
  linkAction = 'link',
  linkTargetPopupId,
  linkDirection = 'right',
  linkOpensNewTab = false,
  onVideoOpen,
  onVideoClose,
  children,
}) => {
  const opensNewTab = linkOpensNewTab && linkAction === 'link';
  const hasAttribution = Boolean(headshotSrc) || name !== undefined || Boolean(title);

  return (
    <div className={['testimonial__content', className].filter(Boolean).join(' ')}>
      {(eyebrow || logoSrc) && (
        <div className="testimonial__eyebrow">
          {logoSrc ? (
            <span className="testimonial__eyebrow-logo">
              <Image bare url={logoSrc} alt={logoAlt ?? (name ? `Brand logo for ${name}` : 'Brand logo')} />
            </span>
          ) : (
            <span className="testimonial__eyebrow-text">{eyebrow}</span>
          )}
        </div>
      )}

      {heading && <h3 className="testimonial__heading">{heading}</h3>}

      <blockquote className="testimonial__quote" data-poly-quote-marks="" ref={markQuoteElement}>
        {stripQuoteMarks(quote)}
      </blockquote>

      {hasAttribution && (
        <div className="testimonial__attribution">
          {headshotSrc && (
            <div className="testimonial__headshot">
              <Image url={headshotSrc} alt={headshotAlt ?? name ?? ''} variant="headshot" />
            </div>
          )}
          <div className="testimonial__author-info">
            {name !== undefined && <div className="testimonial__name">{name}</div>}
            {title && <div className="testimonial__title">{title}</div>}
          </div>
        </div>
      )}

      {linkText && hasCtaDestination(linkHref, linkAction, linkTargetPopupId) && (
        <div className="testimonial__link">
          <ArrowLink
            href={linkHref}
            direction={linkDirection}
            action={linkAction}
            popupTargetId={linkTargetPopupId}
            onVideoOpen={onVideoOpen}
            onVideoClose={onVideoClose}
            {...(opensNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            {linkText}
          </ArrowLink>
        </div>
      )}

      {children}
    </div>
  );
};
